import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Box, Typography, Button, Card, CardActionArea, CardContent, Chip, CircularProgress, Alert, Paper } from '@mui/material';
import { Add, Celebration, Event, Hotel, Museum, Restaurant, AutoAwesome, ChatBubbleOutline, Description, PhoneAndroid } from '@mui/icons-material';
import { conciergeApi } from '../services/concierge-api-client';

const USE_CASE_ICONS: Record<string, React.ReactNode> = { wedding: <Celebration />, event: <Event />, hotel: <Hotel />, museum: <Museum />, restaurant: <Restaurant />, other: <AutoAwesome /> };
const STATUS_COLORS: Record<string, 'success' | 'warning' | 'default'> = { active: 'success', paused: 'warning', archived: 'default' };

export default function ConciergeList() {
  const navigate = useNavigate();
  const { data: concierges, isLoading, error } = useQuery('concierges', () => conciergeApi.listConcierges(), { staleTime: 30_000 });

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>My Concierges</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>Self-serve concierges powered by your documents</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/concierges/new')} sx={{ borderRadius: 3, bgcolor: '#3C5945', '&:hover': { bgcolor: '#2E4635' } }}>Create Concierge</Button>
      </Box>
      {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#569469' }} /></Box>}
      {error && <Alert severity="error" sx={{ borderRadius: 2 }}>Failed to load concierges. Please refresh.</Alert>}
      {!isLoading && !error && concierges?.length === 0 && (
        <Paper sx={{ textAlign: 'center', py: 8, px: 4, borderRadius: 4, border: '1px solid #E5E7EB' }} elevation={0}>
          <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: '#F8FAF9', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
            <AutoAwesome sx={{ fontSize: 36, color: '#569469' }} />
          </Box>
          <Typography variant="h5" fontWeight={700}>Create your first concierge</Typography>
          <Typography color="text.secondary" sx={{ mt: 1, mb: 3, maxWidth: 400, mx: 'auto' }}>Upload your documents, configure the personality, and give your guests a dedicated SMS number.</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/concierges/new')} sx={{ borderRadius: 3, bgcolor: '#3C5945', px: 4, '&:hover': { bgcolor: '#2E4635' } }}>Create Concierge</Button>
        </Paper>
      )}
      {concierges && concierges.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {concierges.map((c: any) => (
            <Card key={c.instance_id} elevation={0} sx={{ borderRadius: 3, border: '1px solid #E5E7EB', transition: 'all 0.2s', '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.08)', transform: 'translateY(-1px)' } }}>
              <CardActionArea onClick={() => navigate(`/concierges/${c.instance_id}`)}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: '#F8FAF9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#569469' }}>{USE_CASE_ICONS[c.use_case] || <AutoAwesome />}</Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={600}>{c.name}</Typography>
                      <Chip label={c.status} color={STATUS_COLORS[c.status] || 'default'} size="small" sx={{ mt: 0.5, height: 22, fontSize: 11 }} />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3, color: 'text.secondary' }}>
                    {c.phone_number && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><PhoneAndroid sx={{ fontSize: 16 }} /><Typography variant="caption">{c.phone_number}</Typography></Box>}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Description sx={{ fontSize: 16 }} /><Typography variant="caption">{c.stats?.total_documents || 0} docs</Typography></Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><ChatBubbleOutline sx={{ fontSize: 16 }} /><Typography variant="caption">{c.stats?.total_messages || 0} msgs</Typography></Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}
